import { useState, useEffect } from 'react';
import { Form, Input, Button, Typography, message, Select, DatePicker, Row, Col } from 'antd';
import {
  UserOutlined,
  LockOutlined,
  MailOutlined,
  PhoneOutlined,
  BankOutlined,
  GlobalOutlined,
  LinkedinOutlined,
  FacebookOutlined,
  ArrowLeftOutlined,
  ArrowRightOutlined,
  CheckOutlined
} from '@ant-design/icons';
import { useNavigate, Link } from 'react-router-dom';
import Lottie from 'lottie-react';
import aiAnalyticsAnimation from '../../assets/ai-analytics-animation.json';
import apiService from '../../services/apiService';
import PricingCards, { PlanName, BillingCycle } from '../../modules/billing/components/PricingCards';

// Responsive styles
const styles = `
  @media (max-width: 768px) {
    .register-container {
      flex-direction: column !important;
    }
    .register-branding {
      display: none !important;
    }
    .register-form-container {
      padding: 24px !important;
    }
    .register-form-container .ant-form {
      max-width: 100% !important;
      width: 100% !important;
    }
    .register-form-container h3 {
      text-align: center !important;
      width: 100% !important;
    }
  }
  @media (max-width: 480px) {
    .register-form-container {
      padding: 16px !important;
    }
  }
`;

const { Title, Text, Paragraph } = Typography;

// Industry options
const industryOptions = [
  { value: 'technology', label: 'Technology' },
  { value: 'finance', label: 'Finance & Banking' },
  { value: 'healthcare', label: 'Healthcare' },
  { value: 'retail', label: 'Retail & E-commerce' },
  { value: 'manufacturing', label: 'Manufacturing' },
  { value: 'education', label: 'Education' },
  { value: 'real_estate', label: 'Real Estate' },
  { value: 'hospitality', label: 'Hospitality & Tourism' },
  { value: 'logistics', label: 'Logistics & Transportation' },
  { value: 'consulting', label: 'Consulting & Professional Services' },
  { value: 'media', label: 'Media & Entertainment' },
  { value: 'other', label: 'Other' },
];

// Employee count options
const employeeCountOptions = [
  { value: '1-10', label: '1-10' },
  { value: '11-50', label: '11-50' },
  { value: '51-200', label: '51-200' },
  { value: '201-500', label: '201-500' },
  { value: '501-1000', label: '501-1000' },
  { value: '1000+', label: '1000+' },
];

// Referral source options
const referralSourceOptions = [
  { value: 'google', label: 'Google' },
  { value: 'linkedin', label: 'LinkedIn' },
  { value: 'facebook', label: 'Facebook' },
  { value: 'instagram', label: 'Instagram' },
  { value: 'friend', label: 'Friend' },
  { value: 'business_partner', label: 'Business Partner' },
  { value: 'trade_show', label: 'Trade Show' },
  { value: 'blog_article', label: 'Blog/Article' },
  { value: 'other', label: 'Other' },
];

// Suggest a clean, memorable Company ID from the company name.
// No random suffix: this is the login key every user in the company types,
// so it must be something they can actually remember. Uniqueness is checked
// against the server before the account is created.
const generateCompanyCode = (companyName: string): string => {
  if (!companyName) return '';
  const cleaned = companyName.trim().replace(/[^A-Za-z0-9\s-]/g, '');
  const words = cleaned.split(/\s+/).filter(Boolean);
  if (words.length === 0) return '';

  // A single word becomes the ID itself (e.g. "Acme" -> "ACME").
  if (words.length === 1) {
    return words[0].toUpperCase().slice(0, 20);
  }

  // Multiple words become initials (e.g. "Acme Trading Ltd" -> "ATL"),
  // falling back to the first word when that is too short to be useful.
  const initials = words.map(w => w.charAt(0).toUpperCase()).join('').slice(0, 20);
  return initials.length >= 3 ? initials : words[0].toUpperCase().slice(0, 20);
};

interface FormData {
  // Step 1: Personal Information
  name: string;
  dateOfBirth?: string;
  email: string;
  phone: string;
  password: string;
  confirmPassword: string;
  // Step 2: Company Information
  companyName: string;
  companyCode: string;
  employeeCount: string;
  industry: string;
  targetSegment?: string;
  website?: string;
  linkedin?: string;
  facebook?: string;
  // Step 3: Plan + promo
  planName?: 'PERSONAL' | 'BUSINESS' | 'BUSINESS_PLUS';
  billingCycle?: 'MONTHLY' | 'YEARLY';
  promotionCode?: string;
  // Step 4: Referral
  referralSource: string;
  referralOther?: string;
}

const RegisterPage = () => {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [currentStep, setCurrentStep] = useState(0);
  const [companyCodeTouched, setCompanyCodeTouched] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState<Partial<FormData>>({});
  // Plan selection state (lives outside the antd form because PricingCards is a custom component)
  const [planName, setPlanName] = useState<PlanName>('BUSINESS');
  const [billingCycle, setBillingCycle] = useState<BillingCycle>('YEARLY');
  const [promotionCode, setPromotionCode] = useState<string>('');
  const [showPromoInput, setShowPromoInput] = useState<boolean>(false);
  const [promoState, setPromoState] = useState<
    | { status: 'idle' }
    | { status: 'checking' }
    | { status: 'valid'; description?: string | null }
    | { status: 'invalid'; reason: string }
  >({ status: 'idle' });

  const handleValidatePromo = async () => {
    const code = promotionCode.trim();
    if (!code) {
      setPromoState({ status: 'invalid', reason: 'Enter a code' });
      return;
    }
    setPromoState({ status: 'checking' });
    try {
      const res: any = await apiService.get(`/users/promotion-codes/validate?code=${encodeURIComponent(code)}`);
      if (res?.success) {
        setPromoState({ status: 'valid', description: res.data?.description });
      } else {
        setPromoState({ status: 'invalid', reason: res?.message || 'Invalid code' });
      }
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.message || 'Invalid code';
      setPromoState({ status: 'invalid', reason: msg });
    }
  };

  // Inject responsive styles
  useEffect(() => {
    const styleElement = document.createElement('style');
    styleElement.textContent = styles;
    document.head.appendChild(styleElement);
    return () => {
      document.head.removeChild(styleElement);
    };
  }, []);

  const steps = [
    { title: 'Personal Information', label: 'Account' },
    { title: 'Company Information', label: 'Company' },
    { title: 'Where did you find us?', label: 'Source' },
    { title: 'Choose Your Plan', label: 'Plan' },
  ];

  // Plan is now the LAST step (index 3) so "Complete Registration"
  // immediately triggers payment without an interrupting source step.
  const isPlanStep = currentStep === 3;

  // Validate current step and proceed
  const handleNext = async () => {
    try {
      // Plan step (now index 3) has no antd form fields — skip validation.
      if (currentStep === 3) {
        setCurrentStep(prev => prev + 1);
        return;
      }
      const values = await form.validateFields();
      setFormData(prev => ({ ...prev, ...values }));
      setCurrentStep(prev => prev + 1);
    } catch (error) {
      // Validation failed
    }
  };

  const handleBack = () => {
    setCurrentStep(prev => prev - 1);
  };

  // Handle company name change to auto-suggest code
  const handleCompanyNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Stop suggesting once the user has edited the Company ID themselves,
    // so we never overwrite their chosen value.
    if (companyCodeTouched) return;
    form.setFieldsValue({ companyCode: generateCompanyCode(e.target.value) });
  };

  // Final submit
  const handleSubmit = async () => {
    try {
      // No form.validateFields here — the Plan step is the final step and has
      // no antd-managed fields (PricingCards drives planName/billingCycle/promo).
      // Earlier steps were validated when the user clicked Next on each.
      const allData = { ...formData };

      setIsLoading(true);

      // Call registration API
      const response: any = await apiService.post('/users/register', {
        // Personal info
        name: allData.name,
        email: allData.email,
        phone: allData.phone,
        password: allData.password,
        dateOfBirth: allData.dateOfBirth,
        // Company info
        companyName: allData.companyName,
        companyCode: String(allData.companyCode || '').trim().toUpperCase(),
        employeeCount: allData.employeeCount,
        industry: allData.industry,
        targetSegment: allData.targetSegment,
        website: allData.website,
        linkedin: allData.linkedin,
        facebook: allData.facebook,
        // Referral
        referralSource: allData.referralSource,
        referralOther: allData.referralOther,
        // Plan + promo
        planName,
        billingCycle,
        promotionCode: promotionCode.trim() || undefined,
      });

      if (response.success) {
        // Auto-login so the create-checkout call is authenticated.
        if (response.token && response.user) {
          localStorage.setItem('authToken', response.token);
          if (response.refreshToken) {
            localStorage.setItem('refreshToken', response.refreshToken);
          }
        }

        // If the backend says payment isn't required (free trial via promo, or a
        // hypothetical $0 plan), go straight to the app.
        const paymentRequired = response.paymentRequired !== false;

        if (!paymentRequired) {
          message.success('Welcome to WYNI AI CRM!');
          window.location.href = '/';
          return;
        }

        // Otherwise redirect to Stripe Checkout. We need the planId, not the name.
        try {
          const plansRes: any = await apiService.get('/ai-crm/subscriptions/plans');
          const plans: any[] = plansRes?.data || [];
          const target = plans.find((p) => p.name === planName);
          if (!target) throw new Error(`Plan "${planName}" not available`);
          const checkoutRes: any = await apiService.post('/ai-crm/subscriptions/create-checkout', {
            planId: target.id,
            billingCycle,
          });
          const url = checkoutRes?.data?.url;
          if (!url) throw new Error('Stripe did not return a checkout URL');
          message.success('Redirecting to secure payment…');
          window.location.assign(url);
          return;
        } catch (checkoutErr: any) {
          message.error(
            checkoutErr?.response?.data?.message ||
              checkoutErr?.message ||
              'Could not open payment page. Sign in and try Switch Plan from My Plan.'
          );
          // Fall through to login so the user has a way back in.
          navigate('/login');
          return;
        }
      } else {
        message.error(response.message || 'Registration failed');
      }
    } catch (error: any) {
      message.error(error.response?.data?.message || error.message || 'Registration failed');
    } finally {
      setIsLoading(false);
    }
  };

  // Step 1: Personal Information
  const renderStep1 = () => (
    <>
      <Form.Item
        name="name"
        label="Name"
        rules={[{ required: true, message: 'Please enter your name' }]}
      >
        <Input
          prefix={<UserOutlined />}
          placeholder="Enter your First and Last name"
          size="large"
        />
      </Form.Item>

      <Form.Item
        name="dateOfBirth"
        label="Date of Birth"
      >
        <DatePicker
          style={{ width: '100%' }}
          placeholder="MM/DD/YY"
          format="MM/DD/YY"
          size="large"
        />
      </Form.Item>

      <Form.Item
        name="email"
        label="Email"
        rules={[
          { required: true, message: 'Please enter your email' },
          { type: 'email', message: 'Please enter a valid email' }
        ]}
      >
        <Input
          prefix={<MailOutlined />}
          placeholder="email@gmail.com"
          size="large"
        />
      </Form.Item>

      <Form.Item
        name="phone"
        label="Phone number"
        rules={[{ required: true, message: 'Please enter your phone number' }]}
      >
        <Input
          prefix={<PhoneOutlined />}
          placeholder="+1 (555) 000-0000"
          size="large"
        />
      </Form.Item>

      <Form.Item
        name="password"
        label="Password"
        rules={[
          { required: true, message: 'Please enter your password' },
          { min: 8, message: 'Password must be at least 8 characters' }
        ]}
      >
        <Input.Password
          prefix={<LockOutlined />}
          placeholder="Create a password"
          size="large"
        />
      </Form.Item>

      <Form.Item
        name="confirmPassword"
        label="Confirm Password"
        dependencies={['password']}
        rules={[
          { required: true, message: 'Please confirm your password' },
          ({ getFieldValue }) => ({
            validator(_, value) {
              if (!value || getFieldValue('password') === value) {
                return Promise.resolve();
              }
              return Promise.reject(new Error('Passwords do not match'));
            },
          }),
        ]}
      >
        <Input.Password
          prefix={<LockOutlined />}
          placeholder="Confirm your password"
          size="large"
        />
      </Form.Item>
    </>
  );

  // Step 2: Company Information
  const renderStep2 = () => (
    <>
      <Form.Item
        name="companyName"
        label="Company Name"
        rules={[{ required: true, message: 'Please enter your company name' }]}
      >
        <Input
          prefix={<BankOutlined />}
          placeholder="Your company name"
          size="large"
          onChange={handleCompanyNameChange}
        />
      </Form.Item>

      <Form.Item
        name="companyCode"
        label="Company ID"
        validateFirst
        validateDebounce={500}
        rules={[
          { required: true, message: 'Please enter a Company ID' },
          {
            pattern: /^[A-Za-z0-9][A-Za-z0-9-]{2,19}$/,
            message: 'Use 3-20 characters: letters, numbers and dashes only',
          },
          {
            // Redundancy check so the user fixes a clash here rather than on submit.
            validator: async (_rule, value) => {
              const code = String(value || '').trim().toUpperCase();
              if (!code) return;
              const res: any = await apiService.get(
                `/users/check-company-code?code=${encodeURIComponent(code)}`
              );
              const data = res?.data || res;
              if (data && data.available === false) {
                throw new Error(data.reason || 'This Company ID is already taken');
              }
            },
          },
        ]}
        extra="You and your team will use this to sign in. Choose something easy to remember."
      >
        <Input
          placeholder="e.g., ACME"
          size="large"
          style={{ textTransform: 'uppercase' }}
          onChange={() => setCompanyCodeTouched(true)}
        />
      </Form.Item>

      <Row gutter={16}>
        <Col span={12}>
          <Form.Item
            name="employeeCount"
            label="No. of Employees"
            rules={[{ required: true, message: 'Please select' }]}
          >
            <Select
              placeholder="Select"
              size="large"
              options={employeeCountOptions}
            />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            name="industry"
            label="Industry"
            rules={[{ required: true, message: 'Please select' }]}
          >
            <Select
              placeholder="Select"
              size="large"
              options={industryOptions}
            />
          </Form.Item>
        </Col>
      </Row>

      <Form.Item
        name="targetSegment"
        label="Target Segment Industry"
      >
        <Input
          placeholder="e.g., B2B SaaS, Enterprise"
          size="large"
        />
      </Form.Item>

      <Form.Item
        name="website"
        label="Company Website (Optional)"
      >
        <Input
          prefix={<GlobalOutlined />}
          placeholder="https://yourcompany.com"
          size="large"
        />
      </Form.Item>

      <Row gutter={16}>
        <Col span={12}>
          <Form.Item
            name="linkedin"
            label="LinkedIn (Optional)"
          >
            <Input
              prefix={<LinkedinOutlined />}
              placeholder="LinkedIn URL"
              size="large"
            />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            name="facebook"
            label="Facebook (Optional)"
          >
            <Input
              prefix={<FacebookOutlined />}
              placeholder="Facebook URL"
              size="large"
            />
          </Form.Item>
        </Col>
      </Row>
    </>
  );

  // Step 3 (new): Pick a plan + optional promotion code
  const renderStepPlan = () => (
    <div style={{ marginBottom: 8 }}>
      <PricingCards
        cycle={billingCycle}
        onCycleChange={setBillingCycle}
        selectedPlan={planName}
        onSelectPlan={setPlanName}
        ctaLabel="Select"
      />

      {/* Promo code — inline link by default; expands to a tight, centered input
          row when clicked. No surrounding gray box (looked like an afterthought). */}
      <div
        style={{
          marginTop: 32,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 8,
        }}
      >
        {showPromoInput ? (
          <div style={{ width: '100%', maxWidth: 440 }}>
            <div style={{ display: 'flex', gap: 8 }}>
              <Input
                size="large"
                value={promotionCode}
                onChange={(e) => {
                  setPromotionCode(e.target.value);
                  setPromoState({ status: 'idle' });
                }}
                placeholder="Promotion code"
                allowClear
                style={{ flex: 1 }}
                onPressEnter={handleValidatePromo}
              />
              <Button
                size="large"
                onClick={handleValidatePromo}
                loading={promoState.status === 'checking'}
              >
                Apply
              </Button>
            </div>
            <div
              style={{
                marginTop: 8,
                fontSize: 12,
                textAlign: 'center',
                color:
                  promoState.status === 'valid'
                    ? '#1B5E3A'
                    : promoState.status === 'invalid'
                    ? '#B42318'
                    : '#5B6478',
                minHeight: 18,
              }}
            >
              {promoState.status === 'valid' && (
                <>✓ Code accepted{promoState.description ? ` — ${promoState.description}` : ''}.</>
              )}
              {promoState.status === 'invalid' && <>{promoState.reason}</>}
              {promoState.status === 'idle' && <>If valid, your overrides unlock after registration.</>}
            </div>
          </div>
        ) : (
          <Button
            type="link"
            onClick={() => setShowPromoInput(true)}
            style={{
              padding: 0,
              color: '#5B6478',
              fontSize: 13,
              height: 'auto',
            }}
          >
            Have a promotion code?
          </Button>
        )}
      </div>

      {/* Legal — shown on the final (Plan) step since that's where the user commits.
          Wording matches the actual flow: paid checkout → Stripe; promo → trial. */}
      <div style={{ marginTop: 24, textAlign: 'center' }}>
        <Text type="secondary" style={{ fontSize: 12 }}>
          By clicking Complete Registration, you agree to our{' '}
          <a href="/terms" target="_blank" rel="noopener noreferrer" style={{ color: '#5B6478', textDecoration: 'underline' }}>
            Terms of Service
          </a>{' '}
          and{' '}
          <a href="/privacy" target="_blank" rel="noopener noreferrer" style={{ color: '#5B6478', textDecoration: 'underline' }}>
            Privacy Policy
          </a>
          . You&rsquo;ll be taken to Stripe to complete payment unless a promotion code grants you a free trial.
        </Text>
      </div>
    </div>
  );

  // Step 4: Referral Source
  const renderStep3 = () => (
    <>
      <Form.Item
        name="referralSource"
        label="Where did you find us?"
        rules={[{ required: true, message: 'Please select how you found us' }]}
      >
        <Select
          placeholder="Select an option"
          size="large"
          options={referralSourceOptions}
        />
      </Form.Item>

      {form.getFieldValue('referralSource') === 'other' && (
        <Form.Item
          name="referralOther"
          label="Please specify"
          rules={[{ required: true, message: 'Please specify' }]}
        >
          <Input
            placeholder="Tell us how you found us"
            size="large"
          />
        </Form.Item>
      )}

    </>
  );

  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        return renderStep1();
      case 1:
        return renderStep2();
      case 2:
        return renderStep3();      // Where did you find us?
      case 3:
        return renderStepPlan();   // Choose Your Plan (last — triggers payment)
      default:
        return null;
    }
  };

  return (
    <div
      className="register-container"
      style={{
        minHeight: '100vh',
        display: 'flex',
        background: '#fff'
      }}
    >
      {/* Left Side - Branding (hidden on Plan step to give pricing cards full width) */}
      <div
        className="register-branding"
        style={{
          display: isPlanStep ? 'none' : 'flex',
          flex: '0 0 45%',
          background: 'linear-gradient(135deg, #e8d5f2 0%, #d4b8e8 100%)',
          flexDirection: 'column',
          justifyContent: 'center',
          padding: '60px',
          position: 'relative'
        }}
      >
        <Title level={2} style={{
          color: '#1a1a2e',
          fontWeight: 700,
          marginBottom: 24,
          lineHeight: 1.3,
          textAlign: 'center'
        }}>
          Generate leads wisely<br></br>and grow your business.
        </Title>

        {/* AI Analytics Animation */}
        <div style={{
          marginTop: 40,
          display: 'flex',
          justifyContent: 'center'
        }}>
          <Lottie
            animationData={aiAnalyticsAnimation}
            loop={true}
            style={{
              width: 400,
              height: 400
            }}
          />
        </div>
      </div>

      {/* Right Side - Form */}
      <div
        className="register-form-container"
        style={{
          flex: '1',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          // Center children horizontally on wide viewports — otherwise the
          // 500px form column sits flush-left in the ~1100px right pane.
          alignItems: 'center',
          padding: '40px 80px',
          overflowY: 'auto'
        }}
      >
        {/* Progress Steps — labeled dots so users know where they are in the flow */}
        <div className="register-steps-wrapper" style={{
          marginBottom: 40,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'flex-start',
          gap: 0,
        }}>
          {steps.map((step, index) => {
            const isActive = index === currentStep;
            const isDone = index < currentStep;
            const dotColor = isActive ? '#0B1220' : isDone ? '#0B1220' : '#D9DEE7';
            const labelColor = isActive ? '#0B1220' : isDone ? '#0B1220' : '#9AA1AE';
            return (
              <div
                key={index}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                }}
              >
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, minWidth: 64 }}>
                  <div
                    style={{
                      width: isActive ? 10 : 8,
                      height: isActive ? 10 : 8,
                      borderRadius: '50%',
                      backgroundColor: dotColor,
                      boxShadow: isActive ? '0 0 0 4px rgba(11,18,32,0.10)' : 'none',
                      transition: 'all 200ms ease',
                    }}
                  />
                  <span
                    style={{
                      fontSize: 11,
                      fontWeight: isActive ? 600 : 500,
                      letterSpacing: '0.06em',
                      textTransform: 'uppercase',
                      color: labelColor,
                      transition: 'color 200ms ease',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {step.label}
                  </span>
                </div>
                {index < steps.length - 1 && (
                  <div
                    style={{
                      width: 40,
                      height: 1,
                      backgroundColor: index < currentStep ? '#0B1220' : '#E5E8EF',
                      marginTop: -16,
                      transition: 'background-color 200ms ease',
                    }}
                  />
                )}
              </div>
            );
          })}
        </div>

        {/* Step Title — uses the display serif on the Plan step to tie the page to the pricing aesthetic.
            Width is constrained to match the form below so left-aligned heading lines up with form fields. */}
        <Title
          level={isPlanStep ? 2 : 3}
          style={{
            marginBottom: isPlanStep ? 8 : 32,
            textAlign: isPlanStep ? 'center' : 'left',
            fontFamily: isPlanStep
              ? '"Fraunces", "Tiempos Headline", Georgia, serif'
              : undefined,
            fontWeight: isPlanStep ? 600 : undefined,
            letterSpacing: isPlanStep ? '-0.02em' : undefined,
            color: isPlanStep ? '#0B1220' : undefined,
            width: '100%',
            maxWidth: isPlanStep ? 1080 : 500,
          }}
        >
          {steps[currentStep].title}
        </Title>
        {isPlanStep && (
          <div
            style={{
              textAlign: 'center',
              fontSize: 14,
              color: '#5B6478',
              marginBottom: 40,
              fontFamily: 'Inter, system-ui, sans-serif',
            }}
          >
            You can change or cancel anytime.
          </div>
        )}

        {/* Form — the Plan step expands to full width since the left branding panel is hidden */}
        <Form
          form={form}
          layout="vertical"
          initialValues={formData}
          style={{
            maxWidth: isPlanStep ? 1080 : 500,
            width: '100%',
            margin: isPlanStep ? '0 auto' : undefined,
          }}
        >
          {renderStepContent()}

          {/* Navigation Buttons */}
          <div style={{
            marginTop: 32,
            display: 'flex',
            justifyContent: 'space-between',
            gap: 16
          }}>
            {currentStep > 0 && (
              <Button
                size="large"
                onClick={handleBack}
                icon={<ArrowLeftOutlined />}
              >
                Back
              </Button>
            )}

            <div style={{ flex: 1 }} />

            {currentStep < steps.length - 1 ? (
              <Button
                type="primary"
                size="large"
                onClick={handleNext}
                style={{
                  background: '#0d6e6e',
                  minWidth: 120
                }}
              >
                Next <ArrowRightOutlined />
              </Button>
            ) : (
              <Button
                type="primary"
                size="large"
                onClick={handleSubmit}
                loading={isLoading}
                icon={<CheckOutlined />}
                style={{
                  background: '#0d6e6e',
                  minWidth: 180
                }}
              >
                Complete Registration
              </Button>
            )}
          </div>

          {/* Login Link */}
          <div style={{ marginTop: 24, textAlign: 'center' }}>
            <Text type="secondary">
              Already have an account?{' '}
              <Link to="/login" style={{ color: '#0d6e6e' }}>
                Log in
              </Link>
            </Text>
          </div>
        </Form>
      </div>
    </div>
  );
};

export default RegisterPage;
